import React, { useContext, useEffect, useState } from 'react';
import { ApiResponse, SuccessApiResponse, ApiException } from '@moodys/cre-cpm-client-apis.apis.api-base';
import { DefaultEmptyStoreContext, StoreContextProps } from '@moodys/cre-cpm.context.store-context';

export interface QueryOptions<T> {
  cacheKey?: string
  disabled?: boolean
  storeContext?: React.Context<StoreContextProps>
  invalideCache?: boolean
  onError?: (kind: unknown) => void
  onSuccess?: (response: SuccessApiResponse<T>) => void
  onComplete?: () => void
}

const useQuery = <T>(
  query: () => Promise<ApiResponse<T>>,
  options?: QueryOptions<T>,
): [T | undefined, boolean, boolean, boolean, () => void, (values: T) => void] => {
  const [data, setData] = useState<T>();
  const [isFetching, setIsFetching] = useState(false);
  const [isInitialFetchDone, setisInitialFetchDone] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { set, get } = useContext(options?.storeContext ?? DefaultEmptyStoreContext);

  const assertParams = () => {
    if (options && options.cacheKey && !options.storeContext) {
      throw Error('useQuery: cache needs a store context to be used');
    }
  };

  const request = async () => {
    setIsFetching(true);
    setHasError(false);
    let result: T | undefined;

    if (!options?.invalideCache && options?.cacheKey) {
      result = get<T>(options.cacheKey);
    }
    if (!result) {
      try {
        const response = await query();

        if (!response) {
          setIsFetching(false);
          return;
        }

        if (response.kind === 'rejected') {
          throw new ApiException(response.kind, response.issues);
        }

        if (response.kind !== 'ok') {
          throw new ApiException(response.kind);
        }

        if (options?.onSuccess) {
          options.onSuccess(response);
        }

        result = response.data;

        if (options?.cacheKey) {
          set(options.cacheKey, result);
        }
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        setHasError(true);
      } finally {
        if (options?.onComplete) {
          options.onComplete();
        }
      }
    }

    setData(result);

    setIsFetching(false);

    setisInitialFetchDone(true);
  };

  useEffect(() => {
    assertParams();

    if (!options?.disabled) {
      request();
    }
    // eslint-disable-next-line
  }, [options?.disabled, options?.invalideCache])

  return [data, isFetching, isInitialFetchDone, hasError, request, setData];
};

export { useQuery };
